import type { ActionInput, ActionOutput } from '@occ/contracts';
import { runAction } from './actions';

/**
 * Servicios y procesos del nodo (Módulo 6.2).
 *
 * Las cuatro acciones del catálogo: `services.list`, `services.control`,
 * `processes.list` y `processes.kill`. Nada más y nada menos, porque el catálogo
 * es la única fuente y el panel no inventa caminos propios (P3).
 */

export type ServiceRow = ActionOutput<'services.list'>['services'][number];
export type ServiceStatus = ServiceRow['status'];
export type ServiceAction = ActionInput<'services.control'>['action'];
export type ProcessRow = ActionOutput<'processes.list'>['processes'][number];

export const listServices = async (nodeId: string): Promise<ServiceRow[]> => {
  const { services } = await runAction('services.list', { node: { nodeId } });
  // Lo que está caído arriba: es lo que se abre esta pantalla a buscar.
  const rank: Record<ServiceStatus, number> = { failed: 0, stopped: 1, unknown: 2, running: 3 };
  return [...services].sort(
    (left, right) =>
      rank[left.status] - rank[right.status] ||
      (left.displayName ?? left.name).localeCompare(right.displayName ?? right.name, 'es'),
  );
};

export const controlService = async (
  nodeId: string,
  service: string,
  action: ServiceAction,
): Promise<ServiceStatus> => {
  const result = await runAction('services.control', { node: { nodeId }, service, action });
  return result.status;
};

/**
 * Procesos del nodo.
 *
 * El agente ya los manda ordenados por memoria descendente y ese orden se
 * respeta: quien abre esta pantalla busca al que se está comiendo la caja. Lo que
 * **no** se hace aquí es recortar la lista —un Windows de escritorio son unos
 * 1800 procesos— porque el recorte es una decisión de la pantalla, que sabe
 * cuántas filas está mostrando y qué ha escrito el operador en el filtro.
 */
export const listProcesses = async (nodeId: string): Promise<ProcessRow[]> => {
  const { processes } = await runAction('processes.list', { node: { nodeId } });
  return processes;
};

/**
 * Nombre con el que enseñar un proceso.
 *
 * El agente devuelve `name: ""` en los procesos protegidos del sistema, de los
 * que sabe el pid y la memoria pero no el nombre. Se enseña así en vez de
 * esconderlos: uno de ellos puede ser el que se está comiendo la memoria, y una
 * fila sin nombre se investiga, una fila que no existe no.
 */
export const processLabel = (process: ProcessRow): string =>
  process.name.trim() === '' ? `(sin nombre · pid ${process.pid})` : process.name;

export const killProcess = async (nodeId: string, pid: number): Promise<void> => {
  await runAction('processes.kill', { node: { nodeId }, pid });
};
